import "scrollyfills";

import { Box, Flex, Spinner } from "@chakra-ui/react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import React, { CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

export interface WindowVirtualGridProps<T> {
  data: T[];
  totalCount: number;
  renderItem: (item: T, index?: number) => React.ReactNode;
  renderItems?: (items: T[]) => React.ReactNode;
  estimateSize?: (index: number) => number;
  measureElement?: (el: HTMLElement, instance: unknown) => number;
  scrollContainerHeight?: CSSProperties["height"];
  scrollContainerWidth?: CSSProperties["width"];
  direction?: "horizontal" | "vertical";
  itemHeight?: number;
  isFetchingNextPage?: boolean;
  onFetchNextPage?: () => void;
  overscan?: number;
  hasNextPage: boolean;
  initialIndex?: number;
  onMeasureScrollContainer?: (rect: DOMRect) => void;
  gap?: number;
  paddingStart?: number;
  paddingEnd?: number;
  onScroll?: (scrollableRef: HTMLDivElement) => void;
  pageBuffer?: number;
  header?: React.ReactNode;
  resetScrollOnPathChange?: boolean;
  /** Callback fired when first visible index changes (for scroll restoration) */
  onVisibleIndexChange?: (index: number) => void;
  /** Callback fired when visible range changes */
  onVisibleRangeChange?: (startIndex: number, endIndex: number) => void;
  /** Index to restore scroll to on back navigation */
  restoreToIndex?: number;
}

export function WindowVirtualGrid<T>({
  renderItem,
  renderItems,
  data,
  totalCount,
  estimateSize = () => 150,
  measureElement,
  direction = "vertical",
  itemHeight,
  isFetchingNextPage,
  onFetchNextPage,
  overscan = 5,
  hasNextPage,
  initialIndex = 0,
  gap = 0,
  paddingStart = 0,
  paddingEnd = 0,
  pageBuffer = 1,
  header,
  resetScrollOnPathChange,
  onVisibleIndexChange,
  onVisibleRangeChange,
  restoreToIndex,
}: WindowVirtualGridProps<T>) {
  const rowVirtualizer = useWindowVirtualizer({
    count: totalCount,
    estimateSize,
    measureElement,
    horizontal: direction === "horizontal",
    overscan,
    gap,
    paddingStart,
    paddingEnd,
  });

  const location = useLocation();
  useEffect(() => {
    if (resetScrollOnPathChange && rowVirtualizer) {
      rowVirtualizer.scrollToIndex(0);
    }
  }, [resetScrollOnPathChange, rowVirtualizer, location]);

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (initialIndex === 0) setInitialized(true);
    if (!initialized && initialIndex) {
      rowVirtualizer.scrollToIndex(initialIndex, { align: "start" });
      setInitialized(true);
    }
  }, [initialIndex, initialized]);

  // Track first visible index for scroll restoration
  const visibleStartIndex = rowVirtualizer.range?.startIndex ?? 0;
  const visibleEndIndex = rowVirtualizer.range?.endIndex ?? visibleStartIndex;
  const lastReportedIndex = useRef(0);
  useEffect(() => {
    if (!onVisibleIndexChange) return;

    // Only report if index changed
    if (visibleStartIndex !== lastReportedIndex.current) {
      lastReportedIndex.current = visibleStartIndex;
      onVisibleIndexChange(visibleStartIndex);
    }
  }, [visibleStartIndex, onVisibleIndexChange]);

  const lastReportedRange = useRef<{ startIndex: number; endIndex: number }>({
    startIndex: 0,
    endIndex: 0,
  });
  useEffect(() => {
    if (!onVisibleRangeChange) {
      return;
    }

    const lastRange = lastReportedRange.current;
    if (
      lastRange.startIndex === visibleStartIndex &&
      lastRange.endIndex === visibleEndIndex
    ) {
      return;
    }

    lastReportedRange.current = {
      startIndex: visibleStartIndex,
      endIndex: visibleEndIndex,
    };
    onVisibleRangeChange(visibleStartIndex, visibleEndIndex);
  }, [visibleStartIndex, visibleEndIndex, onVisibleRangeChange]);

  // Handle scroll restoration on back navigation
  const hasRestoredScroll = useRef(false);
  useEffect(() => {
    if (
      restoreToIndex !== undefined &&
      restoreToIndex > 0 &&
      !hasRestoredScroll.current &&
      totalCount > 0
    ) {
      hasRestoredScroll.current = true;
      // Double RAF ensures the virtualizer has measured elements before scrolling:
      // 1st RAF: browser processes pending layout from data loading
      // 2nd RAF: measurements are complete, safe to scroll
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          rowVirtualizer.scrollToIndex(restoreToIndex, { align: "start" });
        });
      });
    }
  }, [restoreToIndex, totalCount, rowVirtualizer]);

  // Reset restoration flag when restoreToIndex changes (new navigation)
  // Also scroll to top on fresh navigation (when there's no restore target)
  // useLayoutEffect ensures this runs before browser paint, preventing scroll flash
  const hasMounted = useRef(false);
  useLayoutEffect(() => {
    if (restoreToIndex === undefined) {
      hasRestoredScroll.current = false;
      // Only scroll to top on navigation changes, not on initial mount
      if (hasMounted.current) {
        window.scrollTo(0, 0);
      }
      hasMounted.current = true;
    }
  }, [restoreToIndex]);

  const shouldFetchNextPage = useMemo(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();
    const lastIndex = lastItem ? lastItem.index : -1;
    if (lastIndex === -1) return false;
    return lastIndex + pageBuffer >= totalCount;
  }, [rowVirtualizer.getVirtualItems()]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || !onFetchNextPage) {
      return;
    }

    if (shouldFetchNextPage) {
      onFetchNextPage();
    }
  }, [onFetchNextPage, isFetchingNextPage, hasNextPage, shouldFetchNextPage]);

  // Block pointer events while scrolling
  const isVertical = direction === "vertical";

  const containerStyle: CSSProperties = {
    position: "relative",
    transform: "translate3d(0, 0, 0)",
  };

  if (isVertical) {
    containerStyle["height"] = `${rowVirtualizer.getTotalSize()}px`;
    containerStyle["width"] = `100%`;
    // Drop any horizontal overflow that could be caused by scale
    containerStyle["overflowX"] = `hidden`;
  } else {
    containerStyle["height"] = itemHeight ? `${itemHeight}px` : undefined;
    containerStyle["width"] = `${rowVirtualizer.getTotalSize()}px`;
  }

  return (
    <Flex
      direction="column"
      position="relative"
      width="full"
      height="-webkit-fill-available"
    >
      {header}
      <Box style={containerStyle}>
        {renderItems
          ? renderItems(data)
          : rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const rowItem = data[virtualItem.index];

              const virtualItemStyle: CSSProperties = {
                position: "absolute",
                top: 0,
                left: 0,
              };

              const itemSize = Math.floor(virtualItem.size);
              const itemStart = Math.floor(virtualItem.start);
              if (direction === "vertical") {
                virtualItemStyle["width"] = "100%";
                virtualItemStyle["height"] = `${itemSize}px`;
                virtualItemStyle["transform"] = `translateY(${itemStart}px)`;
              } else {
                virtualItemStyle["width"] = `${itemSize}px`;
                virtualItemStyle["transform"] = `translateX(${itemStart}px)`;
              }

              return (
                <Box
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  style={virtualItemStyle}
                  ref={rowVirtualizer.measureElement}
                  overflow="clip"
                >
                  {renderItem(rowItem, virtualItem.index)}
                </Box>
              );
            })}
      </Box>
      {isFetchingNextPage && (
        <Flex
          direction="row"
          alignItems="center"
          justifyContent="center"
          grow={1}
          paddingBottom="16px"
        >
          <Spinner color="blaze.blaze" size="lg" />
        </Flex>
      )}
    </Flex>
  );
}
